import {useEffect,useState,useMemo} from 'react'
import {useDispatch,useSelector} from 'react-redux'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'
import {envAwarePost} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import MAXPortionOptions from '../MAXPortionOptions/MAXPortionOptions'
import {replaceEventData} from '../todaysEvents/TodaysEventsSlice'
import useApiStatusDispatch from '../../hooks/useApiStatusDispatch'
import MAXDiets from './MAXDiets'
import MAXFoodEditor from './MAXFoodEditor'
import {
	getMAXFoodList,
	selectAllFoods,
	selectFoodById,
	toggleDietsModal,
	setEditor,
	removeMAXFood,
	selectAllDiets,
	addFoodToMAXDiet,
	removeFoodFromMAXDiet
} from './MAXSlice'
import classes from './FoodList.module.css'

const MAXFood = ({id})=>{
	const user = useCurrentUser()
	const isChain = user.account_type === 'chain'
	const isClient =  user.account_type === 'client'
	const diets = useSelector(selectAllDiets)
	const dietNameIdMap = {}
	diets.forEach(d=>{
		dietNameIdMap[d.name] = d.id
	})

	//Getting User
	const [isApplying,setIsApplying] = useState(false)
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()

	const food = useSelector(state=>selectFoodById(state,id))
	const [selectedDiets,setSelectedDiets] = useState(food.diets.map(name=>(
		{label:name,value:dietNameIdMap[name]}
	)))

	const handleEditClick = ()=>{
		if(!isChain) return
		dispatch(setEditor({
			show:true,
			mode:'edit',
			id
		}))
	}

	const handleRemove = ()=>{
		if(!window.confirm('Are you sure you want to delete this food?')) return
		statusDispatch(removeMAXFood({id}),'food','delete')
	}

	const handleAddDiet = (diet_id)=>{
		dispatch(addFoodToMAXDiet({
			food_id:id,
			diet_id
		}))
	}

	const handleRemoveDiet = (diet_id)=>{
		dispatch(removeFoodFromMAXDiet({
			food_id:id,
			diet_id
		}))
	}

	return (
		<tr>
			<td>
				<div
					onClick={handleEditClick}  
					className={isChain?'text-primary':'text-dark'}
					style={{
						cursor:isChain?'pointer':'cursor'
					}}
				>
					{food.name}
				</div>
				<div className="d-flex justify-content-between align-items-center flex-wrap">
					<div className={classes.Macros}>
						<span>Protein: {food.protein || 0}</span>
						<span>Carbs: {food.carbs || 0}</span>
						<span>Fruits: {food.fruits || 0}</span>
						<span>Vegetables: {food.vegetables || 0}</span>
						<span>Fat: {food.fat || 0}</span>
					</div>
					{(isClient)&&
						<MAXPortionOptions 
							disabled={isApplying}
							onPortionSelect={(multiplier)=>{
								setIsApplying(true)
								envAwarePost('api/events/api/',{
									action:'logMAXFoodMacros',
									food_id:food.id,
									normalize:1,
									multiplier,
								}).then(res=>{
									setIsApplying(false)
									dispatch(replaceEventData({
										event_type:'nutrition',
										event:res.data,
									}))
								})
							}}>
								<Button 
									className="ml-auto my-1 my-sm-0"
									variant="outline-success"
									size="sm"
									disabled={isApplying}>
									{
										isApplying?
										<div>
											<Spinner 
												animation="border"
												className="mr-2"
												size="sm"
											/>
											please wait
										</div>:
										"Add to Nutrition"
									}
								</Button>
						</MAXPortionOptions>
					}
				</div>
				{isChain&&
					<div>
						<small>Diets</small>
						<Select 
							onChange={(selected,payload)=>{
								if(payload.action==='remove-value'){
									handleRemoveDiet(payload.removedValue.value)
								}else{
									handleAddDiet(payload.option.value)
								}
								setSelectedDiets(selected)
							}}
							value={selectedDiets}
							options={diets.map(d=>({
								label:d.name,
								value:d.id
							}))}
							placeholder="Select diets that can have this food..."
							isMulti
						/>
					</div>
				}
			</td>
			{isChain && (
				<td>
					<div
						className={classes.DeleteIcon}
						style={{ cursor: "pointer" }}
						onClick={handleRemove}>
						X
					</div>
				</td>
			)}
		</tr>
	)
}

const MAXFoodList = ()=>{
	const dispatch = useDispatch()
	const user = useCurrentUser()
	const isChain = user.account_type === 'chain'
	useEffect(()=>{
		dispatch(getMAXFoodList())
	},[dispatch])


	const foods = useSelector(selectAllFoods)
	const [searchQuery,setSearchQuery] = useState('')
	const filteredFoods = useMemo(
		()=> foods.filter( f => (
			searchQuery === '' ||
			f.name.toLowerCase().includes(searchQuery.toLowerCase())
		)),
		[searchQuery,foods]
	)

	const handleToggleDiets = ()=>{
		dispatch(toggleDietsModal())
	}

	const handleAddFood = ()=>{
		dispatch(setEditor({
			mode:'create',
			show:true
		}))
	}

	return (
		<div>
			<div>
				<Form.Control 
					onChange={e=>{
						setSearchQuery(e.target.value)
					}}
					placeholder="search"
				/>
				{isChain&&
					<div className="text-right mt-1">
						<OverlayTrigger overlay={
							<Tooltip>Plant Based, Pescatarian etc</Tooltip>
						}>
							<Button 
								onClick={handleToggleDiets}
								variant="outline-primary" 
								size="sm"
							>
								Manage Food Categories
							</Button>
						</OverlayTrigger>
						<Button 
							onClick={handleAddFood}
							variant="outline-success" 
							className="ml-2"
							size="sm"
						>
							Add Food
						</Button>
					</div>
				}
			</div>
			<Table bordered hover className={classes.foodTable}>
				<thead>
					<tr>
						<th colSpan="100%">Food</th>
					</tr>
				</thead>
            	<tbody>
					{filteredFoods.map( f => <MAXFood id={f.id}/> )}
				</tbody>
			</Table>
			{isChain&&<MAXDiets/>}
			{isChain&&<MAXFoodEditor/>}
		</div>
	)
}

export default MAXFoodList